import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Clock3, ShieldAlert } from 'lucide-react'
import TeamAvatar from './TeamAvatar'
import { useTeamStore } from '../store/useTeamStore'
import useAppStore from '../../../store/useAppStore'
import { modeLabel } from '../utils/teamStats'
import { CARD_CLASS } from '../utils/teamUi'
import { CapturePrize, CaptureProgress, CaptureStatus, useCaptureWindow } from '../pages/TeamCapture'
import { hapticImpact } from '../../../utils/haptic'
import useAudioManager from '../../../hooks/useAudioManager'

const number = (value) => Number(value || 0).toLocaleString()
const CAPTURE_PREVIEW_OPEN_KEY = 'team.capture.previewOpen'

const TeamCapturePreview = ({ mode, className = '' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { playSound } = useAudioManager({ pick: 'pick' })
  const isGuest = useAppStore((state) => state.isGuest)
  const minesEnabled = useAppStore((state) => state.user?.mines_enabled !== false)
  const { capture, captureError, loadCapture } = useTeamStore()
  const clock = useCaptureWindow(capture, loadCapture)
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(CAPTURE_PREVIEW_OPEN_KEY)
      return saved === 'true' || (mode === 'mines' && saved === null)
    } catch {
      return mode === 'mines'
    }
  })

  useEffect(() => { loadCapture() }, [loadCapture, isGuest])

  const item = capture?.modes?.find((entry) => entry.mode === mode)
  const hasMyActivity = Number(item?.my?.energy) > 0 || item?.my?.place != null || item?.my?.is_owner === true
  const hasActivity = Boolean(item?.owner || item?.contenders?.length || Number(item?.teams_count) > 0 || hasMyActivity)
  if (!capture?.enabled || captureError || (mode === 'mines' && !minesEnabled) || (!hasActivity && mode !== 'mines') || !item) return null

  const contenders = item.contenders?.slice(0, 2) || []
  const openRace = () => {
    hapticImpact('light')
    navigate(`/teams/capture/${mode}`)
  }
  const togglePreview = () => {
    hapticImpact('light')
    if (!isOpen) playSound('pick')
    const nextOpen = !isOpen
    setIsOpen(nextOpen)
    try {
      window.localStorage.setItem(CAPTURE_PREVIEW_OPEN_KEY, String(nextOpen))
    } catch {
      return
    }
  }

  return <section className={`${CARD_CLASS} p-4 ${className}`}>
    <h2 className="text-sm font-bold not-italic text-white">
      <button type="button" onClick={togglePreview} aria-expanded={isOpen} className="flex w-full items-center gap-2 text-left active:scale-[0.98]">
        <ShieldAlert size={18} className="shrink-0 text-team-accent" />
        <span className="min-w-0 flex-1">{t('team.capture.title')} · {modeLabel(mode, t)}</span>
        <ChevronDown size={18} className={`shrink-0 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    </h2>
    {isOpen && <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-dark-primary px-2 py-1 text-xs tabular-nums text-white" title={t('team.capture.nextWindow')}><Clock3 size={14} className="text-text-secondary" />{clock}</span>
        {(!capture.payouts || item.prize != null) && <span className={`inline-flex max-w-full items-center rounded-lg border px-2 py-1 text-xs ${capture.payouts ? 'border-accent-primary/30 bg-accent-primary/10' : 'border-white/10 bg-dark-primary'}`} title={t('team.capture.windowPrize')}><CapturePrize payouts={capture.payouts} prize={item.prize} t={t} /></span>}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-dark-primary p-3">
          <p className="mb-2 text-[11px] text-text-secondary">{t('team.capture.currentOwner')}</p>
          {item.owner ? <div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={!item.owner.team?.slug} onClick={() => { hapticImpact('light'); navigate(`/teams/${item.owner.team.slug}`) }} className="flex min-w-0 flex-1 items-center gap-2 text-left enabled:active:scale-[0.98] disabled:cursor-default">
                <TeamAvatar team={item.owner.team} size={32} />
                <span className="min-w-0"><span className="block truncate text-sm font-semibold text-white">{item.owner.team?.name || t('team.capture.currentOwner')}</span><span className="hidden text-xs tabular-nums text-text-secondary">{number(item.owner.energy)}</span></span>
              </button>
              <CaptureStatus status={item.owner.status} t={t} />
            </div>
            <CaptureProgress pct={item.owner.pct} label={item.owner.team?.name || t('team.capture.currentOwner')} />
            {mode === 'mines' && item.owner.streak != null && <p className="mt-2 text-xs text-text-secondary">{t('team.capture.streak', { minutes: item.owner.streak * (capture.window?.length || 900) / 60 })}</p>}
          </div> : <p className="text-sm text-text-secondary">{t('team.capture.free')}</p>}
        </div>
        {contenders.length > 0 && <div className="rounded-xl border border-white/10 bg-dark-primary p-3">
          <p className="mb-2 text-[11px] text-text-secondary">{t('team.capture.race')}</p>
          <div className="space-y-2">
            {contenders.map((contender) => <div key={contender.team?.slug || contender.place}>
              <div className="flex items-center gap-2">
                <button type="button" disabled={!contender.team?.slug} onClick={() => { hapticImpact('light'); navigate(`/teams/${contender.team.slug}`) }} className="flex min-w-0 flex-1 items-center gap-2 text-left enabled:active:scale-[0.98] disabled:cursor-default">
                  <TeamAvatar team={contender.team} size={32} />
                  <span className="min-w-0"><span className="block truncate text-sm font-semibold text-white">{contender.team?.name || t('team.capture.race')}</span><span className="hidden text-xs tabular-nums text-text-secondary">{number(contender.energy)}</span></span>
                </button>
                <CaptureStatus status={contender.status} t={t} />
              </div>
              <CaptureProgress pct={contender.pct} label={contender.team?.name || t('team.capture.race')} />
            </div>)}
          </div>
        </div>}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-white/10 pt-3 text-xs">
        {item.my && <span className="hidden text-text-secondary">{t('team.capture.myPlace', { place: item.my.place ?? '—', energy: number(item.my.energy) })}</span>}
        <button type="button" onClick={openRace} className="ml-auto inline-flex items-center gap-1 font-semibold text-team-accent active:scale-[0.98]">{t('team.capture.race')}<ChevronRight size={16} /></button>
      </div>
    </div>}
  </section>
}

export default TeamCapturePreview
